// src/app/features/authSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { AuthUser } from "../api/authApi";

/* ─────────────────────────────────────────────────────────────────
   STATE
───────────────────────────────────────────────────────────────── */

export interface AuthState {
  token?: string;
  user?: AuthUser;
  hydrated: boolean;
}

/* ─────────────────────────────────────────────────────────────────
   STORAGE
───────────────────────────────────────────────────────────────── */

const AUTH_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const STORAGE_KEY = "auth_state";
const STORAGE_EXPIRES_KEY = "auth_expires";

/* ─────────────────────────────────────────────────────────────────
   INITIAL STATE
───────────────────────────────────────────────────────────────── */

const initialState: AuthState = {
  token: undefined,
  user: undefined,
  hydrated: false,
};

/* ─────────────────────────────────────────────────────────────────
   SLICE
───────────────────────────────────────────────────────────────── */

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Store token + user after login or register
    setCredentials(
      state,
      action: PayloadAction<{ token: string; user: AuthUser }>,
    ) {
      const { token, user } = action.payload;
      state.token = token;
      state.user = user;
      state.hydrated = true;

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, user }));
        localStorage.setItem(
          STORAGE_EXPIRES_KEY,
          String(Date.now() + AUTH_TTL_MS),
        );
      } catch (err) {
        console.warn("auth: failed to persist credentials", err);
      }
    },

    // Patch the stored user after profile updates, tier upgrades, etc.
    // The backend always returns the full updated user — we replace wholesale.
    updateUser(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;

      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ token: state.token, user: action.payload }),
        );
      } catch (err) {
        console.warn("auth: failed to persist user update", err);
      }
    },

    // Called when the backend returns a new tier for the user
    // (e.g. after being selected as a Legacy Fellow).
    // Avoids a full getCurrentUser round-trip in most cases.
    updateTier(
      state,
      action: PayloadAction<{
        tier: AuthUser["tier"];
        fellowsEligible?: boolean;
        fellowsEligibleQuarter?: AuthUser["fellowsEligibleQuarter"];
      }>,
    ) {
      if (!state.user) return;
      state.user.tier = action.payload.tier;
      if (action.payload.fellowsEligible !== undefined) {
        state.user.fellowsEligible = action.payload.fellowsEligible;
      }
      if (action.payload.fellowsEligibleQuarter !== undefined) {
        state.user.fellowsEligibleQuarter =
          action.payload.fellowsEligibleQuarter;
      }

      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ token: state.token, user: state.user }),
        );
      } catch (err) {
        console.warn("auth: failed to persist tier update", err);
      }
    },

    // Patch onboarding-related fields locally so the OnboardingModal and
    // WelcomeModal disappear immediately after the user completes each step —
    // without waiting for a network round-trip or cache invalidation.
    //
    // Persisted to localStorage in the same call so a page refresh before the
    // background invalidatesTags refetch completes still shows the correct state.
    patchOnboarding(
      state,
      action: PayloadAction<{
        region?: string;
        province?: string | null;
        interests?: string[];
        hasCompletedOnboarding?: boolean;
        hasSeenWelcome?: boolean;
      }>,
    ) {
      if (!state.user) return;
      const p = action.payload;
      if (p.region !== undefined) state.user.region = p.region;
      if (p.province !== undefined)
        state.user.province = p.province ?? undefined;
      if (p.interests !== undefined) state.user.interests = p.interests;
      if (p.hasCompletedOnboarding !== undefined)
        state.user.hasCompletedOnboarding = p.hasCompletedOnboarding;
      if (p.hasSeenWelcome !== undefined)
        state.user.hasSeenWelcome = p.hasSeenWelcome;

      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ token: state.token, user: state.user }),
        );
      } catch (err) {
        console.warn("auth: failed to persist onboarding patch", err);
      }
    },

    // Clear auth state and storage (logout)
    clearCredentials(state) {
      state.token = undefined;
      state.user = undefined;
      state.hydrated = true;

      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_EXPIRES_KEY);
      } catch {
        // ignore
      }
    },

    // Load auth state from localStorage on app startup — call once in App.tsx
    loadFromStorage(state) {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const expires = Number(
          localStorage.getItem(STORAGE_EXPIRES_KEY) || "0",
        );

        if (!raw || !expires || Date.now() > expires) {
          state.token = undefined;
          state.user = undefined;
          state.hydrated = true;
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(STORAGE_EXPIRES_KEY);
          return;
        }

        const parsed = JSON.parse(raw) as { token?: string; user?: AuthUser };
        state.token = parsed.token;
        state.user = parsed.user;
        state.hydrated = true;
      } catch {
        state.token = undefined;
        state.user = undefined;
        state.hydrated = true;
      }
    },
  },
});

export const {
  setCredentials,
  updateUser,
  updateTier,
  patchOnboarding,
  clearCredentials,
  loadFromStorage,
} = authSlice.actions;

export default authSlice.reducer;
